import { Fragment } from 'react'

import { TypographyGroupValue } from '@makyoapp/makyo-common'
import { ColorVariant, getTextColorStyles } from '@makyoapp/makyo-tw-ui'

import { CoreComponentAction, CoreComponentBrandkit, CoreComponentInfos } from '@/core/models/app-components/types'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableSelect } from '@/hooks/__project__/useParseVariableSelect'
import { ComponentSettingKey } from '@/shared/builder'
import { parseSpacing } from '@/shared/builder-utils'
import { SpacingInputValue } from '@/shared/builder/types/spacing-input'
import { getSafely } from '@/shared/safely'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

import { CompHeadline } from '../../shared/CompHeadline'
import { CompCircularProgressProps } from './comp'

export interface CompCircularProgressWrapperCoreProps {
  infos: CoreComponentInfos
  action: CoreComponentAction
  brandkit: CoreComponentBrandkit
  children: (props: CompCircularProgressProps) => React.ReactNode
}

/**
 * CompCircularProgressWrapperCore
 *
 * todo: useTypographyStylePropsV2
 */
export const CompCircularProgressWrapperCore = ({
  infos: { settings, containerClient, detailTableId, detailRowIndex, currentParentBg, component },
  children,
}: CompCircularProgressWrapperCoreProps) => {
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()

  // ========================= data =========================
  const maxValue = useParseVariableSelect(
    settings[ComponentSettingKey.CircularProgressDataMaxValue],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const currentValue = useParseVariableSelect(
    settings[ComponentSettingKey.CircularProgressDataValue],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const heading = settings[ComponentSettingKey.CircularProgressDataHeading] ?? []

  const indeterminate = getSafely(settings, [ComponentSettingKey.CircularProgressDataIndeterminate], false)

  const showValue = getSafely(settings, [ComponentSettingKey.CircularProgressDesignShowValue], false)

  // ========================= design =========================

  const size = getSafely(settings, [ComponentSettingKey.CircularProgressDesignSize], ['100px'])[0]
  const compAlignment = getSafely(settings, [ComponentSettingKey.CircularProgressDesignAlignment], 'start')

  const headingAlignment = getSafely(settings, [ComponentSettingKey.CircularProgressDesignHeadingAlignment], 'start')

  const activeIndicatorColor = getSafely(
    settings,
    [ComponentSettingKey.CircularProgressDesignActiveIndicatorColor],
    'primary-40'
  )

  const trackColor = getSafely(settings, [ComponentSettingKey.CircularProgressDesignTrackColor], 'primary-90')

  const labelColorType = settings[ComponentSettingKey.CircularProgressDesignLabelColor]
  const labelColorSolid = settings[ComponentSettingKey.CircularProgressDesignLabelColorSolid] || 'primary-40'
  const labelColorStartGradient =
    settings[ComponentSettingKey.CircularProgressDesignLabelColorStartGradient] || 'primary-40'
  const labelColorEndGradient =
    settings[ComponentSettingKey.CircularProgressDesignLabelColorEndGradient] || 'primary-40'
  const labelColorGradientDirection = settings[ComponentSettingKey.CircularProgressDesignLabelColorGradientDirection]

  const valueColorType = settings[ComponentSettingKey.CircularProgressDesignValueColor]
  const valueColorSolid =
    (settings[ComponentSettingKey.CircularProgressDesignValueColorSolid] as ColorVariant) || 'primary-40'
  const valueColorStartGradient =
    (settings[ComponentSettingKey.CircularProgressDesignValueColorStartGradient] as ColorVariant) || 'primary-40'
  const valueColorEndGradient =
    (settings[ComponentSettingKey.CircularProgressDesignValueColorEndGradient] as ColorVariant) || 'primary-40'
  const valueColorGradientDirection = settings[ComponentSettingKey.CircularProgressDesignValueColorGradientDirection]

  const circleColorType = settings[ComponentSettingKey.CircularProgressDesignCircleColor]
  const circleColorSolid = settings[ComponentSettingKey.CircularProgressDesignCircleColorSolid] || 'primary-40'
  const circleColorStartGradient =
    settings[ComponentSettingKey.CircularProgressDesignCircleColorStartGradient] || 'primary-40'
  const circleColorEndGradient =
    settings[ComponentSettingKey.CircularProgressDesignCircleColorEndGradient] || 'primary-40'
  const circleColorGradientDirection = settings[ComponentSettingKey.CircularProgressDesignCircleColorGradientDirection]

  const margin = settings[ComponentSettingKey.CircularProgressDesignMargin] as SpacingInputValue

  const headingMargin = settings[ComponentSettingKey.CircularProgressDesignHeadingMargin] as SpacingInputValue

  const typoSetting = settings?.[ComponentSettingKey.CircularProgressDesignTypography] as TypographyGroupValue

  const labelTypography = typoSetting?.[ComponentSettingKey.CircularProgressDesignLabelTypography] ?? {
    typography: 'body-medium',
  }

  const headingTypography = typoSetting[ComponentSettingKey.CircularProgressDesignHeadingTypography] ?? {
    typography: 'body-medium',
  }
  const titleComponent = (
    <CompHeadline
      containerClient={containerClient}
      detailTableId={detailTableId}
      detailRowIndex={detailRowIndex}
      headline={heading}
      compTitleAlign={headingAlignment}
      headlineMargin={headingMargin}
      headlineTypo={headingTypography}
      backgroundColor={currentParentBg}
      colorType={labelColorType}
      styles={getTextColorStyles(
        labelColorType,
        labelColorSolid,
        labelColorStartGradient,
        labelColorEndGradient,
        labelColorGradientDirection,
        currentParentBg || 'primary-90'
      )}
    />
  )

  const compSettings: CompCircularProgressProps = {
    maxValue: Number(maxValue),
    currentValue: Number(currentValue),
    indeterminate,
    size,
    compAlignment,
    headingAlignment,
    activeIndicatorColor,
    trackColor,
    margin,
    labelTypography,
    showValue,
    heading,
    titleComponent,
    textColor: currentParentBg ?? 'primary-100',
    valueColorType,
    valueColorSolid,
    valueColorStartGradient,
    valueColorEndGradient,
    valueColorGradientDirection,
    backgroundColor: currentParentBg || 'primary-90',
    circleColorType,
    circleColorSolid,
    circleColorStartGradient,
    circleColorEndGradient,
    circleColorGradientDirection,
  }

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  if (isDevelopmentMode) {
    return (
      <div className='relative flex flex-col w-full border border-dashed' style={{ borderColor: '#333' }}>
        <Fragment>{children(compSettings)}</Fragment>
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return children(compSettings)
}
